/**
 * Pure metric helpers for the admin Owners surfaces (list + detail modal).
 *
 * These are deliberately dependency-free (no supabase, no react-query) so the
 * revenue / invoice-status / onboarding logic that drives the owner KPIs can be
 * unit-tested directly. useOwnerDetail composes them over live data.
 */
import type { RevenueModel } from "@/types/property";
import type { InvoiceSettlement } from "@/types/billing";
import { isOffsetInvoice } from "@/types/billing";

// ─── Onboarding stage ──────────────────────────────────────────────────────

/** Per-property onboarding lifecycle, derived from owner_onboarding_tokens. */
export type OnboardingStage = "pending" | "invite_sent" | "onboarded";

export const ONBOARDING_STAGE_LABEL: Record<OnboardingStage, string> = {
  pending: "Onboarding pending",
  invite_sent: "Onboarding invite sent",
  onboarded: "Onboarded",
};

/** Rank so the "lowest" (least-progressed) stage can be aggregated to owner level. */
export const ONBOARDING_STAGE_RANK: Record<OnboardingStage, number> = {
  pending: 0,
  invite_sent: 1,
  onboarded: 2,
};

/**
 * Per-property onboarding stage. "Onboarded" requires an admin-VERIFIED
 * submission — a completed/signed token alone only counts as "invite sent"
 * until an admin verifies it. No token at all → pending.
 */
export function stageFor(
  hasToken: boolean,
  verified: boolean
): OnboardingStage {
  if (verified) return "onboarded";
  if (hasToken) return "invite_sent";
  return "pending";
}

/** Case/whitespace-insensitive email key. Null for a blank/missing address. */
export function normalizeOwnerEmail(
  email: string | null | undefined
): string | null {
  const e = String(email ?? "")
    .trim()
    .toLowerCase();
  return e === "" ? null : e;
}

/** An onboarding token for one property, with the submissions made through it. */
export type OnboardingTokenWithSubmissions = {
  id: string;
  /** Address the invite went to — this is who the token identifies. */
  email: string | null;
  /**
   * Apartment this invitation covers, or null for a property-wide one (every
   * token predating apartment-scoped onboarding).
   */
  apartment_id?: number | string | null;
  submissions: readonly { status: string; email: string | null }[];
};

/**
 * This owner's onboarding stage FOR ONE PROPERTY.
 *
 * Onboarding tokens hang off properties, but ownership is apartment-level: a
 * property can host apartments belonging to several lessors. Reading the
 * property's stage directly therefore credited every owner in the building with
 * whichever lessor actually onboarded — the Owners list showed "Onboarded"
 * while that owner's Onboarding tab said "No onboarding submission on file".
 *
 * A token counts for this owner only when it is attributable to them:
 *   - the property is one they HOLD (any token on your own property is yours,
 *     even if the invite went to a different address) — EXCEPT an
 *     apartment-scoped token for an apartment held by a different lessor, which
 *     is that lessor's onboarding happening inside this owner's building, or
 *   - the invite email matches theirs, or
 *   - they submitted through it (submission email matches theirs).
 *
 * The apartment carve-out is the mirror image of the bug above: without it, a
 * lessor who onboards their own apartment inside someone else's property makes
 * the property holder read "Onboarded" though they never onboarded anything.
 *
 * No attributable token → "pending", which is the honest reading: this owner
 * has not been invited, whatever the neighbours in the building have done.
 */
export function ownerPropertyOnboardingStage(
  ownerEmail: string | null | undefined,
  /** True when the owner HOLDS this property, not merely hosts an apartment in it. */
  ownsProperty: boolean,
  /** Every token issued for this property. */
  tokens: readonly OnboardingTokenWithSubmissions[],
  /**
   * Narrow to ONE apartment: only that apartment's tokens plus property-wide
   * ones (apartment_id null) count, never a sibling apartment's. Omit for the
   * property-level rollup, where every attributable token counts.
   */
  apartmentId?: number | string | null,
  /**
   * "Is this apartment KNOWN to belong to a different effective owner?" — used
   * to keep a neighbour's apartment-scoped token out of this owner's rollup.
   * Must answer false when the apartment's owner can't be resolved: an unknown
   * apartment falls back to the "your property, your token" rule rather than
   * silently regressing an owner to pending.
   */
  isOtherOwnersApartment?: (apartmentId: string) => boolean
): OnboardingStage {
  const email = normalizeOwnerEmail(ownerEmail);
  const inScope = (t: OnboardingTokenWithSubmissions) => {
    if (apartmentId == null) return true;
    return (
      t.apartment_id == null || String(t.apartment_id) === String(apartmentId)
    );
  };
  /** Scoped to an apartment we know belongs to someone else. */
  const foreignApartment = (t: OnboardingTokenWithSubmissions) =>
    t.apartment_id != null &&
    isOtherOwnersApartment?.(String(t.apartment_id)) === true;
  const mine = tokens.filter(
    (t) =>
      inScope(t) &&
      ((ownsProperty && !foreignApartment(t)) ||
        (email != null &&
          (normalizeOwnerEmail(t.email) === email ||
            t.submissions.some((s) => normalizeOwnerEmail(s.email) === email))))
  );
  if (mine.length === 0) return "pending";
  return stageFor(
    true,
    mine.some((t) => t.submissions.some((s) => s.status === "verified"))
  );
}

/** Least-progressed stage across a set — the owner-level rollup. */
export function lowestOnboardingStage(
  stages: readonly OnboardingStage[]
): OnboardingStage | null {
  if (stages.length === 0) return null;
  return stages.reduce(
    (lowest, s) =>
      ONBOARDING_STAGE_RANK[s] < ONBOARDING_STAGE_RANK[lowest] ? s : lowest,
    stages[0]
  );
}

// ─── Invoice status aggregation ──────────────────────────────────────────────

/**
 * Status shown in the "<Month> invoice" column. No job transitions invoices to
 * "overdue", so a pending invoice past its due date is surfaced as overdue at
 * read time — mirroring managementDisplayStatus.
 *
 * A pending OFFSET invoice reads as "offset", not "pending": its fee is
 * already deducted from the rent CoLiviq collected, so "Pending" here would
 * be the same lie InvoiceStatusPill/StatusBadge were fixed to stop telling —
 * an owner-list reader would see amber "Pending" and read it as money the
 * owner owes. Checked ahead of the overdue derivation (belt-and-braces, same
 * as managementDisplayStatus): the generator never gives an offset row a due
 * date, so this never falls into the overdue branch in practice either way.
 */
export function invoiceDisplayStatus(
  row:
    | {
        status: string;
        due_date: string | null;
        settlement?: InvoiceSettlement | null;
      }
    | undefined,
  now: Date = new Date()
): string | null {
  if (!row) return null;
  if (row.status === "pending" && isOffsetInvoice(row)) return "offset";
  if (row.status === "pending" && row.due_date) {
    const due = new Date(`${String(row.due_date).slice(0, 10)}T23:59:59`);
    if (!Number.isNaN(due.getTime()) && due < now) return "overdue";
  }
  return row.status;
}

/**
 * Severity for "worst" aggregation: overdue is worst, then pending (real money
 * owed), then an unconfirmed offset (nothing owed, but still needs an admin's
 * Confirm-offset action — worth surfacing over an owner whose invoices are
 * simply all paid), then paid.
 */
export function invoiceSeverity(status: string): number {
  return status === "overdue"
    ? 0
    : status === "pending"
      ? 1
      : status === "offset"
        ? 2
        : status === "paid"
          ? 3
          : 4;
}

/** Worst (most severe) display status across a set of invoices, or null if none. */
export function worstInvoiceStatus(
  invs: {
    status: string;
    due_date: string | null;
    settlement?: InvoiceSettlement | null;
  }[],
  now: Date = new Date()
): string | null {
  const displays = invs
    .map((i) => invoiceDisplayStatus(i, now))
    .filter((s): s is string => !!s);
  if (!displays.length) return null;
  return displays.reduce((w, s) =>
    invoiceSeverity(s) < invoiceSeverity(w) ? s : w
  );
}

// ─── Invoice schedule / history split ────────────────────────────────────────

/** How many months ahead the Invoice schedule looks (mirrors tenant rent). */
export const INVOICE_SCHEDULE_MONTHS_AHEAD = 2;

export type OwnerInvoiceLike = {
  /** First day of the covered month (YYYY-MM-01). */
  period_month: string;
  invoice_no: string | null;
  status: string;
  /** Read-time status (pending past its due date reads as overdue). */
  displayStatus?: string;
};

export type OwnerInvoiceSplit<T> = {
  /** Still owed, soonest first — this month through +2 months. */
  schedule: T[];
  /** Settled, newest first. */
  history: T[];
  /** Open invoices dated beyond the schedule window (normally none). */
  laterOpen: T[];
  /** The next invoice actually due — the earliest open one, if any. */
  nextOpen: T | null;
};

/**
 * Split an owner's management invoices into schedule + history, the same way
 * the tenant rent tab splits rent: the schedule is what is still owed (this
 * month and up to two months ahead), the history is what has been paid.
 *
 * Every invoice lands in exactly one bucket — an unpaid invoice from a past
 * period stays in the SCHEDULE (it is still owed; the history is not a place to
 * bury an overdue fee), and `laterOpen` catches the rare invoice dated past the
 * window so no unpaid row can silently disappear. Canceled invoices never
 * reach here: useManagementInvoices excludes them as voided.
 */
export function splitOwnerInvoices<T extends OwnerInvoiceLike>(
  invoices: readonly T[],
  now: Date = new Date()
): OwnerInvoiceSplit<T> {
  const windowEndDate = new Date(
    now.getFullYear(),
    now.getMonth() + INVOICE_SCHEDULE_MONTHS_AHEAD,
    1
  );
  const windowEnd = `${windowEndDate.getFullYear()}-${String(
    windowEndDate.getMonth() + 1
  ).padStart(2, "0")}`;
  const isPaid = (i: T) => (i.displayStatus ?? i.status) === "paid";
  const periodKey = (i: T) => String(i.period_month).slice(0, 7);

  const open = invoices
    .filter((i) => !isPaid(i))
    .sort(
      (a, b) =>
        String(a.period_month).localeCompare(String(b.period_month)) ||
        String(a.invoice_no ?? "").localeCompare(String(b.invoice_no ?? ""))
    );
  const history = invoices
    .filter(isPaid)
    .sort(
      (a, b) =>
        String(b.period_month).localeCompare(String(a.period_month)) ||
        String(b.invoice_no ?? "").localeCompare(String(a.invoice_no ?? ""))
    );

  return {
    schedule: open.filter((i) => periodKey(i) <= windowEnd),
    history,
    laterOpen: open.filter((i) => periodKey(i) > windowEnd),
    // The next invoice is the next OPEN one, so a still-unpaid current-month
    // fee is never skipped in favour of a future month's projection.
    nextOpen: open[0] ?? null,
  };
}

/** An invoice a projection can be anchored on. */
export type OwnerProjectableInvoice = OwnerInvoiceLike & {
  lessor_id: string | null;
  fee_basis?: Record<string, unknown> | null;
  /** Which unit the invoice bills. NULL/NULL is a legacy consolidated row. */
  apartment_id?: number | null;
  property_id?: number | null;
  /**
   * How the invoice settles. Part of the forecast's suppression key: a
   * property's transfer invoice must not hide the offset one that bills its
   * other flats, since the two are separate documents by CHECK constraint.
   */
  settlement?: string | null;
};

/**
 * Fields a projected month must NOT inherit from its anchor invoice.
 *
 * `fee_basis` is the important one: the generator's fee_basis describes ONE
 * month — `properties[]` holds that month's per-property `fee_eur`, and
 * managementInvoiceLines() itemises off it while reconciling NETTO against
 * `amount_cents`. Carried onto another month it would itemise the wrong
 * properties AND the document's NETTO column would not foot. `description`
 * names the property count that produced that breakdown, so it is equally
 * month-specific. Everything else here is evidence of an invoice that exists:
 * a projection has no number, no due date, and nothing paid or sent.
 */
const PROJECTED_INVOICE_FIELDS = {
  __projected: true as const,
  invoice_no: null,
  // Not a real DB status — "Scheduled" reads distinctly from a real pending
  // invoice, which the amber InvoiceStatusPill fallback covers.
  status: "scheduled",
  displayStatus: "scheduled",
  fee_basis: {},
  description: null,
  due_date: null,
  issue_date: null,
  paid_at: null,
  proof_url: null,
  proof_uploaded_at: null,
  invoice_pdf_url: null,
  sent_at: null,
  metadata: {},
  // A forecast is a month's first invoice: when the anchor is a remainder
  // (COL-483), its place in that month's chain must not carry over.
  remainder_seq: 0,
  remainder_of: null,
};

/** `ym` (YYYY-MM) shifted by `n` months. */
function addMonthsYm(ym: string, n: number): string {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7)) - 1 + n;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** One unit `generate_management_invoices` bills as its own invoice. */
export type OwnerBillableUnit = {
  /** NULL for a property-level unit — rooms belonging to no apartment. */
  apartmentId: number | null;
  propertyId: number;
  /** Qualified label ("Whg. 1 · 3 OG – Haus A") — what the document is named. */
  title: string;
  /** The apartment alone ("Whg. 1 · 3 OG"), for a UI that shows the building itself. */
  name: string;
  propertyTitle: string | null;
  collectionMode: string | null;
  /** Effective (apartment→property) fee config, as resolveUnitFeeConfig gives it. */
  config: UnitFeeConfig;
  /** The GENERATOR's occupancy reading, so a forecast matches what it will bill. */
  occupiedRooms: number;
  occupiedRent: number;
  /** True when the occupancy read failed — don't assert "no occupied rooms". */
  degraded: boolean;
};

/** What a projected row carries about the unit it forecasts. */
export type ProjectedUnitFields = {
  __projected: true;
  __unitApartmentId: number | null;
  __unitPropertyId: number;
  __unitTitle: string;
  __unitName: string;
  __unitPropertyTitle: string | null;
  /** False when this unit earns nothing — the row shows the reason, not an amount. */
  __unitBillable: boolean;
  __unitReason: NonBillableReasonCode | null;
  /**
   * How the invoice this row forecasts will be settled — "offset" for a
   * company_collects unit, whose fee is deducted from the rent CoLiviq
   * collects. Carried on the projection so the forecast row reads exactly like
   * the invoice it becomes, rather than the UI re-deriving it from config.
   */
  __unitSettlement: "transfer" | "offset";
};

/**
 * Read-only placeholders for every PROPERTY × month in the schedule window
 * (this month through +2) that has no real invoice yet.
 *
 * Management invoices are generated one month at a time by the monthly cron, so
 * a freshly billed owner has no future rows at all and the schedule would read
 * as empty when billing is in fact ongoing.
 *
 * ONE ROW PER (PROPERTY, SETTLEMENT), NOT PER APARTMENT
 *   Since 20260911120000 the generator folds a property's billable units into a
 *   single document, split only where merging would be wrong: a transfer
 *   invoice (due_date set) and an offset invoice (due_date NULL) cannot share
 *   one row. The forecast has to be shaped like the document it forecasts —
 *   projecting per apartment would show four rows for a month that will produce
 *   one invoice, and then show both the forecasts AND the real invoice once it
 *   is issued, because no per-apartment forecast would match a property-level
 *   row.
 *
 * WHY THIS IS COMPUTED, NOT COPIED FORWARD
 *   The previous version anchored the whole month on the owner's most recent
 *   invoice and reused its amount. That worked only while an owner had exactly
 *   ONE invoice per month, and an owner whose history is a legacy CONSOLIDATED
 *   invoice has no per-property amount anywhere to copy — the forecast has to
 *   come from the current portfolio.
 *
 *   So the amount is computed PER UNIT through `unitMonthlyRevenue` — the same
 *   model math the generator runs, fed by the GENERATOR's occupancy reading —
 *   and only then summed, because full_rent nets against each unit's OWN master
 *   rent. Forecast and issued invoice therefore agree by construction rather
 *   than by coincidence, down to the fee_basis itemisation the row expands to.
 *
 * NON-BILLABLE PROPERTIES ARE STILL LISTED, at 0,00 € with the reason. Their
 * absence is what confused admins before: an onboarded building that bills
 * nothing simply vanished, so a two-property owner read as a one-property
 * owner. The reason is the FIRST of its units' reasons in the same priority
 * order the per-unit row used, since a property that bills nothing usually
 * fails for one reason across the board.
 *
 * MONTHS ALREADY BILLED ARE SKIPPED, mirroring each of the generator's own
 * guards, or the schedule would show a forecast next to the invoice it
 * predicted:
 *   - a live CONSOLIDATED invoice (apartment_id AND property_id both NULL)
 *     covers the whole portfolio, so the month is skipped outright;
 *   - a live PER-APARTMENT invoice (the 20260904120000..20260911120000 window)
 *     suppresses its WHOLE property for the month, exactly as the generator's
 *     ALREADY BILLED PER APARTMENT guard skips the group;
 *   - a live property-level invoice suppresses its own (property, settlement).
 *
 * Returns [] when the owner has never been invoiced: `anchor` supplies the row
 * shape (id, lessor, currency) a projection is spread onto, and "empty until
 * billing exists" remains the correct read for a brand-new owner.
 */
export function projectOwnerPropertyInvoices<T extends OwnerProjectableInvoice>(
  invoices: readonly T[],
  units: readonly OwnerBillableUnit[],
  now: Date = new Date()
): (T & ProjectedUnitFields)[] {
  if (invoices.length === 0 || units.length === 0) return [];
  const anchor = [...invoices].sort((a, b) =>
    String(b.period_month).localeCompare(String(a.period_month))
  )[0];
  if (!anchor.lessor_id) return [];

  const live = invoices.filter((i) => i.status !== "canceled");
  const ym = (i: { period_month: string }) =>
    String(i.period_month).slice(0, 7);
  // Months whose whole portfolio is already covered by one consolidated row.
  const consolidatedMonths = new Set(
    live.filter((i) => i.apartment_id == null && i.property_id == null).map(ym)
  );
  // (month, property) pairs billed per apartment during the split window. One
  // such invoice blocks the property's whole group, which is what the
  // generator does too — it cannot grow an issued document a line.
  const billedPerApartment = new Set(
    live
      .filter((i) => i.apartment_id != null && i.property_id != null)
      .map((i) => `${ym(i)}|${i.property_id}`)
  );
  // (month, property, settlement) triples that already have a real invoice.
  const billedGroups = new Set(
    live
      .filter((i) => i.apartment_id == null && i.property_id != null)
      .map((i) => `${ym(i)}|${i.property_id}|${i.settlement ?? "transfer"}`)
  );

  const thisMonthYm = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;
  const months: string[] = [];
  for (let n = 0; n <= INVOICE_SCHEDULE_MONTHS_AHEAD; n++) {
    months.push(addMonthsYm(thisMonthYm, n));
  }

  /** What one unit contributes to its property's forecast. */
  type PricedUnit = {
    unit: OwnerBillableUnit;
    fee: number;
    settlement: "transfer" | "offset";
    billable: boolean;
    reason: NonBillableReasonCode | null;
  };
  const priced: PricedUnit[] = [...units]
    .sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { numeric: true })
    )
    .map((u) => {
      // Mirrors the generator's only filter: a positive fee. Since
      // 20260908140000 BOTH collection modes are billed — a company_collects
      // unit's fee is invoiced and settled by offset against the rent CoLiviq
      // collected, rather than not invoiced at all — so the mode decides how
      // the row settles, not whether it exists. A zero fee still produces no
      // document, in either mode. The per-unit offset-invoicing switch that
      // used to gate this as well was retired in 20260924120200: offset
      // invoicing now follows company_collects, so there is nothing left to
      // read.
      const fee = unitMonthlyRevenue(u.config, u.occupiedRooms, u.occupiedRent);
      const settlement: "transfer" | "offset" =
        u.collectionMode === "owner_collects" ? "transfer" : "offset";
      const billable = fee > 0;
      return {
        unit: u,
        fee,
        settlement,
        billable,
        // Not routed through nonBillableReason any more: that helper's first
        // rule is "company_collects earns nothing", which stopped being true
        // when both modes started billing. Three reasons remain for a unit the
        // generator will skip, none of them depending on the mode: an
        // occupancy read that failed (don't assert an empty flat we never
        // verified), an occupied full_rent unit with no master rent on file
        // (the generator refuses to bill the whole rent as a margin; the fix
        // is to enter the master rent, so say so rather than "no occupied
        // rooms"), or a fee that genuinely computes to zero.
        reason: billable
          ? null
          : u.degraded
            ? "degraded"
            : u.occupiedRooms > 0 && fullRentMasterRentUnset(u.config)
              ? "master_rent_unset"
              : "no_occupancy",
      } as PricedUnit;
    });

  // Property order is the order their forecasts appear in, so a building sits
  // in the same place every month.
  const byProperty = new Map<number, PricedUnit[]>();
  for (const p of priced) {
    const list = byProperty.get(p.unit.propertyId);
    if (list) list.push(p);
    else byProperty.set(p.unit.propertyId, [p]);
  }

  const rows: (T & ProjectedUnitFields)[] = [];

  for (const month of months) {
    if (consolidatedMonths.has(month)) continue;
    for (const [propertyId, unitsOfProperty] of byProperty) {
      if (billedPerApartment.has(`${month}|${propertyId}`)) continue;
      // The building as the document names it. An apartment unit carries it as
      // `propertyTitle`; a property-level unit (rooms in no apartment) IS the
      // property, so its own `name` is the title.
      const propertyTitle =
        unitsOfProperty[0].unit.propertyTitle ?? unitsOfProperty[0].unit.name;
      const billable = unitsOfProperty.filter((p) => p.billable);

      /** Emit one forecast document. */
      const push = (
        settlement: "transfer" | "offset",
        members: PricedUnit[],
        isBillable: boolean,
        reason: NonBillableReasonCode | null
      ) => {
        if (billedGroups.has(`${month}|${propertyId}|${settlement}`)) return;
        const totalFee = members.reduce((s, m) => s + m.fee, 0);
        const summary = `Management fee for ${propertyTitle}`;
        rows.push({
          ...anchor,
          ...PROJECTED_INVOICE_FIELDS,
          id: `projected-${anchor.lessor_id}-${month}-p${propertyId}-${settlement}`,
          period_month: `${month}-01`,
          amount_cents: isBillable ? Math.round(totalFee * 100) : 0,
          settlement,
          apartment_id: null,
          property_id: propertyId,
          // The itemisation the row expands to, written in the generator's own
          // fee_basis shape so a forecast and the invoice it becomes reveal the
          // same breakdown. Only billable units are listed: a 0,00 € line would
          // be noise the generator never writes either.
          fee_basis: isBillable
            ? {
                level: "property",
                property_count: 1,
                unit_count: members.length,
                occupied_rooms: members.reduce(
                  (s, m) => s + m.unit.occupiedRooms,
                  0
                ),
                occupied_rent_eur: members.reduce(
                  (s, m) => s + m.unit.occupiedRent,
                  0
                ),
                summary,
                collection_mode: members[0]?.unit.collectionMode ?? null,
                settlement,
                // Ordered the way the generator's jsonb_agg is
                // (ORDER BY apartment_id NULLS FIRST), so a forecast and the
                // invoice it becomes list their lines identically.
                properties: [...members]
                  .sort((a, b) => {
                    const x = a.unit.apartmentId;
                    const y = b.unit.apartmentId;
                    if (x === y) return 0;
                    if (x == null) return -1;
                    if (y == null) return 1;
                    return x - y;
                  })
                  .map((m) => ({
                    property_id: propertyId,
                    property_title: propertyTitle,
                    apartment_id: m.unit.apartmentId,
                    apartment_label: m.unit.title,
                    unit_name: m.unit.name,
                    fee_eur: Math.round(m.fee * 100) / 100,
                    occupied_rooms: m.unit.occupiedRooms,
                    occupied_rent_eur: m.unit.occupiedRent,
                    revenue_model: m.unit.config.revenue_model,
                    coliviq_fee_pct: m.unit.config.coliviq_fee_pct,
                    coliviq_fee_amount: m.unit.config.coliviq_fee_amount,
                  })),
              }
            : {},
          __unitApartmentId: null,
          __unitPropertyId: propertyId,
          __unitTitle: propertyTitle,
          __unitName: propertyTitle,
          __unitPropertyTitle: null,
          __unitBillable: isBillable,
          __unitSettlement: settlement,
          __unitReason: reason,
        } as unknown as T & ProjectedUnitFields);
      };

      if (billable.length === 0) {
        // Nothing to bill this month. One row, carrying the first unit's
        // reason — the settlement is whatever that unit would have settled by,
        // so the placeholder occupies the slot the real invoice would have.
        const first = unitsOfProperty[0];
        push(first.settlement, [], false, first.reason);
        continue;
      }
      // A transfer and an offset invoice are two documents; the generator
      // cannot merge them and neither can the forecast.
      for (const settlement of ["transfer", "offset"] as const) {
        const members = billable.filter((p) => p.settlement === settlement);
        if (members.length === 0) continue;
        push(settlement, members, true, null);
      }
    }
  }
  return rows;
}

/**
 * What one row contributes to its group's header chips.
 *
 * Deliberately coarser than InvoiceStatus: the header answers "does anything
 * here need me?", so `offset` (a fee awaiting an admin's confirmation, never a
 * debt the owner will transfer) stays apart from `open` (money genuinely owed),
 * and everything settled collapses into `paid`.
 */
export type OwnerInvoiceRowState = "paid" | "overdue" | "offset" | "open";

export type OwnerInvoiceGroupCounts = Record<OwnerInvoiceRowState, number>;

/** One property's own invoices — the unit the Invoices tab renders a card for. */
export type OwnerInvoicePropertyGroup<T> = {
  /** Stable key: the property, else the legacy portfolio. */
  key: string;
  /** The property, which owns the card. */
  name: string;
  /** The city, shown under the name. Null for a legacy portfolio group. */
  propertyTitle: string | null;
  rows: T[];
  /**
   * Distinct apartments billed across the group's rows, first-seen order, for
   * the card's "n apartments billed" caption. Only BILLABLE rows contribute, so
   * the caption never counts a flat the 0,00 € placeholder rows exist to
   * explain. Empty for a legacy portfolio group, whose rows name no single unit.
   */
  unitNames: string[];
  /**
   * Row states, for the header chips.
   *
   * This REPLACES the summed `totalCents` the header used to show. That figure
   * added paid, unsettled and offset months together, so it named no quantity
   * anyone could act on — an owner who had settled everything and one who owed
   * three months read identically. Counts stay true when the card is collapsed,
   * which is what makes collapsing safe.
   *
   * FORECAST ROWS ARE NOT COUNTED. A projected month is a row the generator has
   * not written yet, so counting it as "open" would report a debt that does not
   * exist. The chips describe what has actually been issued; `invoiceCount`
   * still covers both, which is why the two can differ.
   */
  counts: OwnerInvoiceGroupCounts;
  /**
   * How many of the rows are actual invoices — issued or forecast. Excludes
   * the 0,00 € rows that exist to say why a unit earns nothing this month:
   * counting those as invoices overstates what the owner is billed. Includes
   * forecasts, which `counts` deliberately does not.
   */
  invoiceCount: number;
  /** True when the group holds at least one invoice that actually exists. */
  hasReal: boolean;
};

/**
 * Split a month-ordered list of invoices into one group per PROPERTY.
 *
 * Since 20260911120000 the generator issues one document per property per
 * month, so a building is one card holding its months — the unit an admin
 * already thinks in ("what is Daimlerstrasse costing?"). Invoices issued during
 * the per-apartment window (20260904120000 .. 20260911120000) group by their
 * property too, so an owner's history reads continuously across the change
 * rather than splintering into a card per flat for those months only.
 *
 * Rows keep the order they arrived in (period), so a group reads as one
 * building's billing history top to bottom.
 *
 * ORDER: groups holding a REAL invoice come first, then forecast-only groups,
 * each alphabetically. An admin's page one has to start with what actually
 * exists — these lists are paged by group, so ordering on name alone could push
 * the owner's only issued invoice onto page two behind three forecasts.
 *
 * Legacy portfolio invoices, which belong to no single property, collect in one
 * group of their own rather than being forced under a building they only partly
 * cover; it sorts last among its tier.
 */
export function groupOwnerInvoicesByProperty<
  T extends {
    property_id?: number | null;
    amount_cents: number;
  },
>(
  rows: readonly T[],
  describe: (row: T) => {
    name: string;
    propertyTitle: string | null;
    /** The apartment this row bills, when it bills exactly one. */
    unitName: string | null;
    billable: boolean;
    /** True for a forecast row — a group of only these has nothing issued. */
    projected: boolean;
  },
  stateOf: (row: T) => OwnerInvoiceRowState
): OwnerInvoicePropertyGroup<T>[] {
  const groups = new Map<string, OwnerInvoicePropertyGroup<T>>();
  for (const row of rows) {
    const key = row.property_id != null ? `p${row.property_id}` : "portfolio";
    const d = describe(row);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name: d.name,
        propertyTitle: d.propertyTitle,
        rows: [],
        unitNames: [],
        counts: { paid: 0, overdue: 0, offset: 0, open: 0 },
        invoiceCount: 0,
        hasReal: false,
      };
      groups.set(key, group);
    }
    group.rows.push(row);
    group.hasReal = group.hasReal || !d.projected;
    if (d.billable) {
      group.invoiceCount += 1;
      if (!d.projected) group.counts[stateOf(row)] += 1;
      if (d.unitName && !group.unitNames.includes(d.unitName))
        group.unitNames.push(d.unitName);
    }
  }
  const rank = (g: OwnerInvoicePropertyGroup<T>) =>
    (g.hasReal ? 0 : 2) + (g.key === "portfolio" ? 1 : 0);
  // numeric:true so "Haus 2" sorts before "Haus 10" — titles are numbered by
  // hand and a plain string compare reads them in an order no admin expects.
  return Array.from(groups.values()).sort(
    (a, b) =>
      rank(a) - rank(b) ||
      a.name.localeCompare(b.name, undefined, { numeric: true })
  );
}

export type InvoicedMonthTrend = {
  /** Name of the month being compared against, e.g. "July". */
  label: string;
  /** Invoiced total (cents) for the current month. */
  current: number;
  /** Invoiced total (cents) for the previous month. */
  prevTotal: number;
  delta: number;
  /** Percent change, or null when the previous month invoiced nothing. */
  pct: number | null;
  direction: "up" | "down" | "flat";
};

/**
 * Month-over-month change in what an owner was INVOICED (paid or not) — the
 * same period-over-period comparison the analytics hero shows, scoped to one
 * owner. Null when neither month has an invoice, so the chip stays hidden
 * rather than reading "0 % vs <month>" on an owner with no billing yet.
 */
export function invoicedMonthTrend(
  invoices: readonly { period_month: string; amount_cents: number }[],
  now: Date = new Date(),
  locale = "en-GB"
): InvoicedMonthTrend | null {
  const key = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const currentKey = key(now);
  const prevKey = key(prevDate);
  const sumFor = (k: string) =>
    invoices
      .filter((i) => String(i.period_month).slice(0, 7) === k)
      .reduce((s, i) => s + (i.amount_cents || 0), 0);

  const current = sumFor(currentKey);
  const prevTotal = sumFor(prevKey);
  if (current === 0 && prevTotal === 0) return null;

  const delta = current - prevTotal;
  return {
    label: prevDate.toLocaleDateString(locale, { month: "long" }),
    current,
    prevTotal,
    delta,
    pct: prevTotal > 0 ? (delta / prevTotal) * 100 : null,
    direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
  };
}

/** First month beyond the schedule window, as YYYY-MM-01 (for the "later" note). */
export function invoiceScheduleWindowEnd(now: Date = new Date()): string {
  const d = new Date(
    now.getFullYear(),
    now.getMonth() + INVOICE_SCHEDULE_MONTHS_AHEAD,
    1
  );
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

// ─── Ownership resolution (apartment-level portfolios) ───────────────────────

/**
 * The owner of an apartment, in precedence order:
 *   1. its own lessor  (apartments.lessor_id)
 *   2. the lessor behind its owner ACCOUNT (apartments.owner_user_id) — the
 *      apartment editor's default path, where "the owner is the lessor";
 *      resolve the user → lessor with fetchLessorIdByOwnerUser()
 *   3. the lessor holding its property (properties.lessor_id)
 *
 * Consequences worth spelling out: an apartment reassigned to another lessor
 * leaves the property owner's portfolio, and an apartment inside someone
 * else's property joins its own lessor's.
 *
 * ON THE THIRD ARGUMENT: it is optional, and callers that omit it are relying
 * on `apartments.lessor_id` already being canonical — which it is, because the
 * `apartments_fill_lessor_from_owner` trigger (migration 20260802100500) fills
 * it from owner_user_id on write. Step 2 is the app-side safety net for rows
 * written before that trigger existed, or whose owner account maps to several
 * lessors and was deliberately left unfilled. Drop the trigger and every
 * two-argument call site silently loses those apartments, so don't.
 *
 * `generate_management_invoices` bills COALESCE(apartments.lessor_id,
 * properties.lessor_id) — the same first and third steps. That is exactly why
 * the trigger normalises into lessor_id instead of teaching each consumer
 * about owner accounts: the portfolio you see and the invoice that goes out
 * are then reading the same column.
 */
export function effectiveApartmentOwner(
  apartmentLessorId: string | null | undefined,
  propertyLessorId: string | null | undefined,
  /** Lessor resolved from apartments.owner_user_id, when there is one. */
  ownerAccountLessorId?: string | null
): string | null {
  return apartmentLessorId ?? ownerAccountLessorId ?? propertyLessorId ?? null;
}

/**
 * Whether a room counts toward an owner's portfolio: rooms inherit ownership
 * from their apartment, and only rooms with NO apartment fall back to the
 * property. So a room in another lessor's apartment inside this owner's
 * property is NOT theirs, and a room in their apartment inside another
 * lessor's property IS — matching how the DB generator splits units.
 *
 * Ids are compared as strings: room/apartment/property ids are bigint and
 * PostgREST doesn't narrow them to JS numbers consistently across queries.
 */
export function roomBelongsToOwner(
  room: { apartment_id: number | string | null; property_id: number | string },
  ownedApartmentIds: ReadonlySet<string>,
  ownedPropertyIds: ReadonlySet<string>
): boolean {
  return room.apartment_id != null
    ? ownedApartmentIds.has(String(room.apartment_id))
    : ownedPropertyIds.has(String(room.property_id));
}

// ─── Monthly revenue (mirrors the DB fee generator) ──────────────────────────

/** The fee config that determines CoLiviq's cut for one billable unit. */
export type UnitFeeConfig = {
  revenue_model: RevenueModel | null;
  coliviq_fee_pct: number | null;
  coliviq_fee_amount: number | null;
  master_rent: number | null;
};

/**
 * Resolve the EFFECTIVE fee config for a unit, inheriting apartment → property
 * exactly like the DB `generate_management_invoices` generator:
 *   - revenue model = the apartment's if it sets one, else the property's;
 *   - fee pct / amount travel with whichever level supplied the model;
 *   - master rent = the apartment's if set, else the property's.
 * A room with no apartment (apt == null) resolves to the property config — so
 * properties without apartment overrides behave exactly as before.
 */
export function resolveUnitFeeConfig(
  apt: UnitFeeConfig | null | undefined,
  prop: UnitFeeConfig
): UnitFeeConfig {
  if (apt && apt.revenue_model != null) {
    return {
      revenue_model: apt.revenue_model,
      coliviq_fee_pct: apt.coliviq_fee_pct,
      coliviq_fee_amount: apt.coliviq_fee_amount,
      master_rent: apt.master_rent ?? prop.master_rent,
    };
  }
  return {
    revenue_model: prop.revenue_model,
    coliviq_fee_pct: prop.coliviq_fee_pct,
    coliviq_fee_amount: prop.coliviq_fee_amount,
    master_rent: apt?.master_rent ?? prop.master_rent,
  };
}

/**
 * A full_rent unit with no master rent on file. Its fee is the sublease margin
 * (rent − master rent), and `rent − 0` cannot be told apart from "the master
 * rent was never entered" — both would bill the ENTIRE rent as CoLiviq's fee.
 * The generator (20260907110000) therefore bills such a unit nothing, and so
 * does every forecast here. A genuinely zero master rent is not an arrangement
 * this product has; an owner paid nothing is a flat_fee/pct setup.
 */
export function fullRentMasterRentUnset(config: UnitFeeConfig): boolean {
  return (
    config.revenue_model === "full_rent" &&
    (config.master_rent == null || config.master_rent <= 0)
  );
}

/**
 * CoLiviq's monthly revenue for ONE billable unit, given its effective config,
 * occupied-room count and occupied rent — mirroring the DB generator's amount:
 *   flat_fee_per_room : flat fee × occupied rooms
 *   pct_of_rent       : occupied rent × fee%
 *   full_rent         : occupied rent − master rent (never negative), and 0
 *                       outright while no master rent is on file — see
 *                       fullRentMasterRentUnset.
 */
export function unitMonthlyRevenue(
  config: UnitFeeConfig,
  occupiedRooms: number,
  occupiedRent: number
): number {
  switch (config.revenue_model) {
    case "flat_fee_per_room":
      return (config.coliviq_fee_amount ?? 0) * occupiedRooms;
    case "pct_of_rent":
      return occupiedRent * ((config.coliviq_fee_pct ?? 0) / 100);
    case "full_rent":
      if (fullRentMasterRentUnset(config)) return 0;
      return Math.max(occupiedRent - (config.master_rent ?? 0), 0);
    default:
      return 0;
  }
}

/** One occupied billable unit within a property (apartment-level or property-level). */
export type PropertyUnit = {
  /** Effective (apartment→property) fee config for this unit. */
  config: UnitFeeConfig;
  occupiedRooms: number;
  occupiedRent: number;
};

/**
 * CoLiviq's monthly revenue from a whole property = the sum over its occupied
 * units, each billed with its effective config. A property with no apartment
 * overrides collapses to a single property-level unit, matching the old
 * property-only calculation.
 */
export function propertyMonthlyRevenue(units: PropertyUnit[]): number {
  return units.reduce(
    (sum, u) =>
      sum + unitMonthlyRevenue(u.config, u.occupiedRooms, u.occupiedRent),
    0
  );
}

// ─── Per-property breakdown of one invoice ───────────────────────────────────

/**
 * One property's share of a management invoice, as the Invoices tab expands it.
 *
 * `billable: false` is a property the owner HOLDS that earned no fee this
 * month — it is not in the invoice's breakdown at all. Those rows exist because
 * their absence is what confused admins: an onboarded apartment that bills
 * nothing simply vanished from the schedule, so a two-property owner read as a
 * one-property owner. See ownerInvoicePropertyLines.
 */
export type OwnerInvoicePropertyLine = {
  propertyId: number | null;
  /**
   * The apartment this line bills, when it bills one.
   *
   * Since 20260911120000 a single invoice itemises several apartments of the
   * SAME property, so propertyId no longer identifies a line — every line of a
   * consolidated invoice carries the same one. Callers key their rows off this
   * as well, or React sees duplicate keys down the breakdown.
   */
  apartmentId: number | null;
  title: string;
  /** This property's share of the invoice gross, in cents. 0 when not billable. */
  feeCents: number;
  billable: boolean;
  /** Why a non-billable property earns nothing, for the row's status cell. */
  reason: NonBillableReasonCode | null;
};

/** The per-property rows `generate_management_invoices` writes into fee_basis. */
type FeeBasisProperty = {
  property_id?: number | string;
  property_title?: string;
  /** Set on every line of a per-apartment or consolidated invoice. */
  apartment_id?: number | string | null;
  /** The apartment on its own, e.g. "Whg. 1 · 3 OG". */
  unit_name?: string;
  fee_eur?: number | string;
};

/**
 * True when `feeBasis` carries a real per-property split — i.e. this invoice
 * is a genuine POST-CONSOLIDATION row, not a legacy per-property invoice
 * (before 20260727120000/20260729140000) or a hand-created MANUAL_FEE_BASIS
 * one.
 *
 * This matters beyond `ownerInvoicePropertyLines` (which already falls back
 * safely to one line): a caller deciding whether to append OTHER held
 * properties as "not billed" must not do so for a legacy row, because a
 * multi-property owner's legacy months are billed as several SEPARATE
 * per-property invoice rows for the same period — every "sibling" property
 * already has its own real invoice elsewhere in the table, not a gap this
 * one failed to cover.
 */
export function hasFeeBasisPropertyBreakdown(
  feeBasis: Record<string, unknown> | null | undefined
): boolean {
  return Array.isArray((feeBasis ?? {}).properties);
}

/**
 * The per-property lines of ONE management invoice.
 *
 * The generator consolidates a lessor's whole month into a single invoice
 * (property_id NULL) and puts the split in `fee_basis.properties[]` — one entry
 * per property, each with its own `fee_eur`. That breakdown is the only place
 * the per-property amounts exist, so this reads it rather than recomputing a
 * fee the invoice might not agree with.
 *
 * `amountCents` is authoritative: per-property fees are rounded independently
 * in the DB, so summing them can differ from the invoice total by a cent. The
 * LAST line absorbs the difference (the same reconciliation rule
 * managementInvoiceLines uses on the PDF's NETTO column), so the expanded rows
 * always add up to the amount shown on the month row.
 *
 * An invoice with no breakdown — a legacy per-property row, or one an admin
 * created by hand (MANUAL_FEE_BASIS) — yields a single line for the whole
 * total under `fallbackTitle`. It is not a consolidated invoice, so there is
 * nothing to split.
 */
export function ownerInvoicePropertyLines(
  feeBasis: Record<string, unknown> | null | undefined,
  amountCents: number,
  fallbackTitle: string,
  fallbackPropertyId?: number | null
): OwnerInvoicePropertyLine[] {
  const props = Array.isArray((feeBasis ?? {}).properties)
    ? ((feeBasis as Record<string, unknown>).properties as FeeBasisProperty[])
    : [];
  // A hand-created invoice (manualUnitFeeBasis) lists its unit only to NAME
  // the line; it records no fee_eur, since its amount is whatever the admin
  // typed. Reading that entry as a 0,00 € line made the whole amount look like
  // an edit the breakdown no longer foots (COL-487), so it is one line carrying
  // the invoice's own amount, titled by the unit it names.
  if ((feeBasis ?? {}).level === "manual") {
    const unit = props[0];
    return [
      {
        propertyId:
          unit?.property_id != null && Number.isFinite(Number(unit.property_id))
            ? Number(unit.property_id)
            : (fallbackPropertyId ?? null),
        apartmentId:
          unit?.apartment_id != null &&
          Number.isFinite(Number(unit.apartment_id))
            ? Number(unit.apartment_id)
            : null,
        title:
          String(unit?.unit_name ?? "").trim() ||
          String(unit?.property_title ?? "").trim() ||
          fallbackTitle,
        feeCents: amountCents,
        billable: true,
        reason: null,
      },
    ];
  }
  const parsed = props
    .map((p) => ({
      propertyId: Number.isFinite(Number(p.property_id))
        ? Number(p.property_id)
        : null,
      apartmentId: Number.isFinite(Number(p.apartment_id))
        ? Number(p.apartment_id)
        : null,
      // The APARTMENT names the line where the entry has one. A consolidated
      // invoice's entries all share a property_title, so titling by that would
      // print the building's name once per flat and tell the reader nothing
      // about what they are being charged for — which is the entire reason the
      // breakdown is worth expanding. Falls back to the property for a legacy
      // portfolio invoice, whose entries genuinely are one per building.
      title:
        String(p.unit_name ?? "").trim() ||
        String(p.property_title ?? "").trim() ||
        fallbackTitle,
      feeCents: Math.round(Number(p.fee_eur ?? 0) * 100),
    }))
    .filter((p) => Number.isFinite(p.feeCents));

  if (parsed.length === 0)
    return [
      {
        propertyId: fallbackPropertyId ?? null,
        apartmentId: null,
        title: fallbackTitle,
        feeCents: amountCents,
        billable: true,
        reason: null,
      },
    ];

  const summed = parsed.reduce((s, p) => s + p.feeCents, 0);
  const drift = amountCents - summed;
  // Absorb ROUNDING only. Per-property fees are rounded independently in the
  // DB, so their sum can miss the gross by at most a cent per line — that much
  // is safe to load onto the last row so the rows foot to the invoice.
  //
  // A larger gap means something else: an admin edited amount_cents (editInvoice
  // writes the amount and leaves fee_basis alone), so the recorded per-property
  // fees now describe a different total. Spreading THAT onto one property would
  // invent a number no one ever billed. Leave the recorded fees exactly as
  // they are and let the caller surface the mismatch — see
  // ownerInvoiceBreakdownDrift.
  const absorb = Math.abs(drift) <= parsed.length ? drift : 0;
  return parsed.map((p, i) => ({
    ...p,
    feeCents: i === parsed.length - 1 ? p.feeCents + absorb : p.feeCents,
    billable: true,
    reason: null,
  }));
}

/** A property the owner holds, as the "why did this earn nothing" check sees it. */
export type OwnerHeldProperty = {
  propertyId: number;
  title: string;
  /** Effective collection mode across the owner's units in this property. */
  collectionMode: string | null;
  /**
   * True when at least one owner_collects unit here computes a positive
   * generator fee. Deliberately not just "has an occupied room": a unit whose
   * revenue-model math legitimately computes to zero (e.g. a 0% rate) is
   * exactly as unbillable to the generator as an empty one, and the caller
   * (nonBillableReason) has no separate code for that — it reads as
   * "no_occupancy" too, which is the closest available reason rather than a
   * false "billing gap".
   */
  hasOccupancy: boolean;
  /** True when the occupancy read failed — don't assert "no occupied rooms". */
  degraded: boolean;
  /**
   * True when an occupied owner_collects full_rent unit here has no master
   * rent on file (fullRentMasterRentUnset) — the generator skips it rather
   * than bill the whole rent, and "no occupied rooms" would be the wrong
   * explanation for a full building. Optional: callers that predate the
   * guard, and tests, leave it unset.
   */
  masterRentUnset?: boolean;
};

/**
 * A stable code for why a held property earns no management fee. A code, not
 * a display string, so the caller can translate it — this module has no
 * access to `t()`, and a hardcoded English string here would bypass every
 * other label the Invoices tab renders through it.
 */
export type NonBillableReasonCode =
  | "company_collects"
  | "no_occupancy"
  | "master_rent_unset"
  | "degraded";

/**
 * Why a property the owner holds contributes no management fee, or null when
 * it should have. Mirrors the two filters `generate_management_invoices`
 * applies before it bills a unit: `mode = 'owner_collects'` and a positive fee
 * (which needs an occupied room).
 *
 * Returns a generic reason rather than guessing when occupancy is unknown: a
 * degraded bookings read must not be reported to an admin as "no occupied
 * rooms", which is a claim about the data we did not actually verify.
 */
export function nonBillableReason(
  p: OwnerHeldProperty
): NonBillableReasonCode | null {
  if (p.collectionMode === "company_collects") return "company_collects";
  if (p.degraded) return "degraded";
  if (!p.hasOccupancy) {
    return p.masterRentUnset ? "master_rent_unset" : "no_occupancy";
  }
  return null;
}

/**
 * The invoice's own lines, followed by every property the owner holds that the
 * invoice does NOT cover — each at 0,00 € with the reason it earns nothing.
 *
 * This is the fix for "two apartments onboarded, one invoice": the second
 * property was never missing from the UI by accident, it was filtered out by
 * the generator for a reason the admin could not see. Now the row is there and
 * says why.
 *
 * A property whose absence has no explanation (owner_collects, occupied, yet
 * unbilled) is still listed — with a null reason the caller renders as a
 * warning, because that one IS a billing bug rather than a configuration.
 */
export function ownerInvoicePropertyBreakdown(
  lines: readonly OwnerInvoicePropertyLine[],
  held: readonly OwnerHeldProperty[]
): OwnerInvoicePropertyLine[] {
  const covered = new Set(
    lines.map((l) => (l.propertyId == null ? null : String(l.propertyId)))
  );
  const missing = held
    .filter((p) => !covered.has(String(p.propertyId)))
    .map((p) => ({
      propertyId: p.propertyId,
      // A whole property earned nothing, so no apartment is named — these
      // rows exist to say the BUILDING was not billed.
      apartmentId: null,
      title: p.title,
      feeCents: 0,
      billable: false,
      reason: nonBillableReason(p),
    }));
  return [...lines, ...missing.sort((a, b) => a.title.localeCompare(b.title))];
}

/**
 * How far the breakdown's own fees fall short of (or exceed) the invoice
 * total, in cents. 0 whenever the rows foot — which is the normal case, since
 * ownerInvoicePropertyLines absorbs sub-cent-per-line rounding.
 *
 * A non-zero result means the recorded per-property fees no longer describe
 * this invoice's amount: editInvoice writes amount_cents without touching
 * fee_basis, so an edited invoice keeps the ORIGINAL month's split. Surfacing
 * the gap is the honest move — the alternative is a breakdown that quietly
 * disagrees with the total printed directly above it.
 */
export function ownerInvoiceBreakdownDrift(
  lines: readonly OwnerInvoicePropertyLine[],
  amountCents: number
): number {
  const billed = lines
    .filter((l) => l.billable)
    .reduce((s, l) => s + l.feeCents, 0);
  return amountCents - billed;
}
